import Phaser from "phaser";
import {
  TILE_SIZE,
  WORLD_WIDTH,
  WORLD_HEIGHT,
  UNCHARTED_WATERS_BUFFER_PX,
  FishingSpotType,
} from "../types";
import { TilesetManager } from "../managers/TilesetManager";
import { FishingSpotInfo } from "./FishingSpotRenderer";

export interface ResourceNodeInfo {
  id: string;
  tileX: number;
  tileY: number;
  type: string;
  worldX: number;
  worldY: number;
}

export interface DecorativeFishCallbacks {
  onFishSpotClick?: (spot: FishingSpotInfo) => void;
  onRockMineClick?: (node: ResourceNodeInfo) => void;
}

export class OceanTileRenderer {
  private scene: Phaser.Scene;
  private tilesetManager: TilesetManager | null = null;
  private container: Phaser.GameObjects.Container | null = null;
  private renderedTiles: Map<string, Phaser.GameObjects.Sprite> = new Map();
  private decorSprites: Map<string, Phaser.GameObjects.Sprite> = new Map();
  private fishSprites: Map<string, Phaser.GameObjects.Sprite> = new Map();
  private fishHitAreas: Map<string, Phaser.GameObjects.Rectangle> = new Map();
  private rockHitAreas: Map<string, Phaser.GameObjects.Rectangle> = new Map();
  private decorContainer: Phaser.GameObjects.Container | null = null;
  private fishContainer: Phaser.GameObjects.Container | null = null;
  private lastCameraX: number = -1;
  private lastCameraY: number = -1;
  private renderDistance: number = 30;
  private contextCallback: (() => "core" | "sanctum" | "rift") | null = null;
  private isTileBlockedCallback:
    | ((tileX: number, tileY: number) => boolean)
    | null = null;
  private animationInitialized: boolean = false;
  private currentAnimationFrame: number = 0;
  private animationTimer: number = 0;
  private animationSpeed: number = 150; // ms per frame
  private callbacks: DecorativeFishCallbacks = {};
  private tilesNeedAnimationUpdate: boolean = false;
  private lastCameraCheckX: number = -1;
  private lastCameraCheckY: number = -1;
  private cameraMovementThreshold: number = TILE_SIZE;

  constructor(scene: Phaser.Scene, callbacks?: DecorativeFishCallbacks) {
    this.scene = scene;
    if (callbacks) {
      this.callbacks = callbacks;
    }
  }

  public setCallbacks(callbacks: DecorativeFishCallbacks): void {
    this.callbacks = callbacks;
  }

  public setTilesetManager(manager: TilesetManager): void {
    this.tilesetManager = manager;
  }

  public setContextCallback(callback: () => "core" | "sanctum" | "rift"): void {
    this.contextCallback = callback;
  }

  public setTileBlockedCallback(
    callback: (tileX: number, tileY: number) => boolean,
  ): void {
    this.isTileBlockedCallback = callback;
  }

  public update(
    viewX: number,
    viewY: number,
    viewWidth: number,
    viewHeight: number,
    deltaTime: number = 16,
  ): void {
    if (!this.tilesetManager) {
      return;
    }

    const hasOceanAnimation =
      this.tilesetManager.hasTileTexture("ocean_animation");
    if (!hasOceanAnimation) {
      return;
    }

    // Initialize animation if needed
    if (!this.animationInitialized) {
      this.initializeAnimation();
    }

    // Update animation frame and mark tiles for update if frame changed
    this.updateAnimationFrame(deltaTime);

    // Only recalculate visible tiles if camera moved significantly
    const cameraMovedX = Math.abs(viewX - this.lastCameraCheckX) >= this.cameraMovementThreshold;
    const cameraMovedY = Math.abs(viewY - this.lastCameraCheckY) >= this.cameraMovementThreshold;

    if (!cameraMovedX && !cameraMovedY) {
      return;
    }

    this.lastCameraCheckX = viewX;
    this.lastCameraCheckY = viewY;

    // Calculate visible tile range with larger buffer to cover entire screen
    let startTileX = Math.floor(viewX / TILE_SIZE) - this.renderDistance;
    let endTileX =
      Math.floor((viewX + viewWidth) / TILE_SIZE) + this.renderDistance;
    let startTileY = Math.floor(viewY / TILE_SIZE) - this.renderDistance;
    let endTileY =
      Math.floor((viewY + viewHeight) / TILE_SIZE) + this.renderDistance;

    // Clamp to world bounds plus uncharted buffer
    const minTileX = Math.floor(-UNCHARTED_WATERS_BUFFER_PX / TILE_SIZE);
    const minTileY = Math.floor(-UNCHARTED_WATERS_BUFFER_PX / TILE_SIZE);
    const maxTileX = Math.floor(
      (WORLD_WIDTH + UNCHARTED_WATERS_BUFFER_PX) / TILE_SIZE,
    );
    const maxTileY = Math.floor(
      (WORLD_HEIGHT + UNCHARTED_WATERS_BUFFER_PX) / TILE_SIZE,
    );

    startTileX = Math.max(minTileX, startTileX);
    endTileX = Math.min(maxTileX, endTileX);
    startTileY = Math.max(minTileY, startTileY);
    endTileY = Math.min(maxTileY, endTileY);

    // Track which tiles should be visible
    const visibleTiles = new Set<string>();

    // Create or update ocean tiles in visible range
    for (let tx = startTileX; tx <= endTileX; tx++) {
      for (let ty = startTileY; ty <= endTileY; ty++) {
        // Skip tiles blocked by islands - don't render ocean underneath
        if (this.isTileBlockedCallback?.(tx, ty)) {
          continue;
        }

        const key = `${tx},${ty}`;
        visibleTiles.add(key);

        if (!this.renderedTiles.has(key)) {
          this.createOceanTile(tx, ty);
        }
      }
    }

    this.updateDecorSprites(startTileX, endTileX, startTileY, endTileY);

    // Remove tiles that are no longer visible
    const tilesToRemove: string[] = [];
    this.renderedTiles.forEach((sprite, key) => {
      if (!visibleTiles.has(key)) {
        sprite.destroy();
        tilesToRemove.push(key);
      }
    });
    tilesToRemove.forEach((key) => this.renderedTiles.delete(key));
  }

  private initializeAnimation(): void {
    if (this.animationInitialized) return;
    this.animationInitialized = true;
    this.currentAnimationFrame = 0;
    this.animationTimer = 0;
  }

  private updateAnimationFrame(deltaTime: number): void {
    this.animationTimer += deltaTime;

    if (this.animationTimer >= this.animationSpeed) {
      this.animationTimer = 0;
      this.currentAnimationFrame = (this.currentAnimationFrame + 1) % 5;
      this.tilesNeedAnimationUpdate = true;

      // Batch update all rendered tiles with new frame on next frame
      if (this.tilesNeedAnimationUpdate && this.renderedTiles.size > 0) {
        const textureKey = `ocean_animation_${this.currentAnimationFrame}`;
        if (this.scene.textures.exists(textureKey)) {
          this.renderedTiles.forEach((sprite) => {
            sprite.setTexture(textureKey);
          });
        }
        this.tilesNeedAnimationUpdate = false;
      }
    }
  }

  private createOceanTile(tileX: number, tileY: number): void {
    // Always use animated ocean frames
    const textureKey = `ocean_animation_${this.currentAnimationFrame}`;

    if (!this.scene.textures.exists(textureKey)) {
      console.warn(`🌊 OceanTileRenderer: Texture not found for ${textureKey}`);
      return;
    }

    // Position tiles at world coordinates (they will be visible when in camera view)
    const worldX = tileX * TILE_SIZE;
    const worldY = tileY * TILE_SIZE;

    const sprite = this.scene.add.sprite(worldX, worldY, textureKey);
    sprite.setOrigin(0, 0);
    sprite.setDepth(-8);
    sprite.setScrollFactor(1, 1); // Ensure sprite scrolls with camera

    this.renderedTiles.set(`${tileX},${tileY}`, sprite);
  }

  private updateDecorSprites(
    startTileX: number,
    endTileX: number,
    startTileY: number,
    endTileY: number,
  ): void {
    if (!this.tilesetManager) return;

    const visibleDecor = new Set<string>();
    const visibleFish = new Set<string>();

    for (let tx = startTileX; tx <= endTileX; tx++) {
      for (let ty = startTileY; ty <= endTileY; ty++) {
        if (this.isTileBlockedCallback?.(tx, ty)) {
          continue;
        }

        const decorKey = `${tx},${ty}`;
        const decorSeed = this.getDeterministicRandom(tx, ty);

        if (decorSeed < 0.02) {
          visibleDecor.add(decorKey);
          if (!this.decorSprites.has(decorKey)) {
            // Use modulo 12 to select from available 12 rock variants (01-12)
            const variantIndex = Math.floor(decorSeed * 10000) % 12;
            const variant = `ocean_rock_${String(variantIndex + 1).padStart(2, "0")}`;
            const texture = this.tilesetManager.getTileTexture(variant);
            if (texture) {
              if (!this.decorContainer) {
                this.decorContainer = this.scene.add.container(0, 0);
                this.decorContainer.setDepth(3); // Above island tiles/props
              }
              const worldX = tx * TILE_SIZE + TILE_SIZE / 2;
              const worldY = ty * TILE_SIZE + TILE_SIZE / 2;
              const sprite = this.scene.add.sprite(worldX, worldY, texture);
              sprite.setOrigin(0.5, 0.5);
              sprite.setDepth(3); // Above island tiles/props
              sprite.setScrollFactor(1, 1);
              const scale = 0.75 + ((decorSeed * 4000) % 0.25);
              sprite.setScale(scale);
              this.decorContainer.add(sprite);
              this.decorSprites.set(decorKey, sprite);

              // Create interactive hitarea for mining this rock
              this.createRockHitArea(tx, ty, worldX, worldY, decorKey);
            }
          }
        }

        const fishSeed = this.getDeterministicRandom(tx + 991, ty - 421);
        if (fishSeed < 0.006) {
          visibleFish.add(decorKey);
          if (!this.fishSprites.has(decorKey)) {
            const fishSet = fishSeed < 0.0001 ? "fish_1" : "fish_2";
            const texture = this.tilesetManager.getTileTexture(
              `${fishSet}_frame_1`,
            );
            if (texture) {
              if (!this.fishContainer) {
                this.fishContainer = this.scene.add.container(0, 0);
                this.fishContainer.setDepth(4); // Above ocean decor/rocks and islands
              }
              const worldX = tx * TILE_SIZE + TILE_SIZE / 2;
              const worldY = ty * TILE_SIZE + TILE_SIZE / 2;
              const sprite = this.scene.add.sprite(worldX, worldY, texture);
              sprite.setOrigin(0.5, 0.5);
              sprite.setDepth(4); // Above ocean decor/rocks and islands
              sprite.setScrollFactor(1, 1);
              sprite.setScale(0.8);
              sprite.setAlpha(0.0);
              this.fishContainer.add(sprite);
              this.fishSprites.set(decorKey, sprite);

              // Create interactive hitarea for this fish
              this.createFishHitArea(tx, ty, worldX, worldY, decorKey);

              this.scheduleFishJump(sprite, fishSet, decorKey);
            }
          }
        }
      }
    }

    const visibleRocks = new Set<string>();
    this.decorSprites.forEach((_, key) => {
      visibleRocks.add(key);
    });

    this.pruneDecorSprites(visibleDecor, visibleFish, visibleRocks);
  }

  private scheduleFishJump(
    sprite: Phaser.GameObjects.Sprite,
    fishSet: string,
    key: string,
  ): void {
    const startDelay = 1000 + Math.random() * 2000;
    const frameRate = 5;
    const frames = [1, 2, 3, 4]
      .map((frame) => ({
        key:
          this.tilesetManager?.getTileTexture(`${fishSet}_frame_${frame}`) ||
          "",
      }))
      .filter((frame) => frame.key);

    if (frames.length !== 4) return;

    if (!this.scene.anims.exists(`${fishSet}_jump`)) {
      this.scene.anims.create({
        key: `${fishSet}_jump`,
        frames,
        frameRate,
        repeat: 0,
      });
    }

    this.scene.time.addEvent({
      delay: startDelay,
      callback: () => {
        // Check if sprite still exists and hasn't been destroyed
        if (!this.fishSprites.has(key) || !sprite || !sprite.scene) return;
        sprite.setAlpha(1);
        sprite.play(`${fishSet}_jump`, true);
        // Animation already includes the jump in the art, no need to add bobbing
        this.scene.time.addEvent({
          delay: 700,
          callback: () => {
            // Check again if sprite still exists before stopping
            if (!this.fishSprites.has(key) || !sprite || !sprite.scene) return;
            sprite.setAlpha(0);
            sprite.stop();
            this.scheduleFishJump(sprite, fishSet, key);
          },
        });
      },
    });
  }

  private createFishHitArea(
    tileX: number,
    tileY: number,
    worldX: number,
    worldY: number,
    key: string,
  ): void {
    const hitArea = this.scene.add.rectangle(
      worldX,
      worldY,
      TILE_SIZE,
      TILE_SIZE,
      0x000000,
      0,
    );
    hitArea.setInteractive();
    hitArea.setDepth(6);

    hitArea.on(
      "pointerdown",
      (
        pointer: Phaser.Input.Pointer,
        _localX: number,
        _localY: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        event.stopPropagation();

        if (pointer.leftButtonDown()) {
          // Left-click: start fishing at this fish spot
          if (this.callbacks.onFishSpotClick) {
            const spotInfo: FishingSpotInfo = {
              id: `fish_decor_${key}`,
              tileX,
              tileY,
              type: "shallow_waters" as FishingSpotType,
              worldX,
              worldY,
            };
            this.callbacks.onFishSpotClick(spotInfo);
          }
        }
      },
    );

    // Hover effects
    hitArea.on("pointerover", () => {
      // Could add a visual indicator here if desired
    });

    hitArea.on("pointerout", () => {
      // Clean up any hover indicator
    });

    this.fishHitAreas.set(key, hitArea);
  }

  private createRockHitArea(
    tileX: number,
    tileY: number,
    worldX: number,
    worldY: number,
    key: string,
  ): void {
    const hitArea = this.scene.add.rectangle(
      worldX,
      worldY,
      TILE_SIZE,
      TILE_SIZE,
      0x000000,
      0,
    );
    hitArea.setInteractive();
    hitArea.setDepth(6);

    hitArea.on(
      "pointerdown",
      (
        pointer: Phaser.Input.Pointer,
        _localX: number,
        _localY: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        event.stopPropagation();

        if (pointer.leftButtonDown()) {
          // Left-click: start mining at this rock
          if (this.callbacks.onRockMineClick) {
            const nodeInfo: ResourceNodeInfo = {
              id: `rock_decor_${key}`,
              tileX,
              tileY,
              type: "stone_quarry",
              worldX,
              worldY,
            };
            this.callbacks.onRockMineClick(nodeInfo);
          }
        }
      },
    );

    // Hover effects
    hitArea.on("pointerover", () => {
      // Could add a visual indicator here if desired
    });

    hitArea.on("pointerout", () => {
      // Clean up any hover indicator
    });

    this.rockHitAreas.set(key, hitArea);
  }

  private pruneDecorSprites(
    visibleDecor: Set<string>,
    visibleFish: Set<string>,
    visibleRocks: Set<string>,
  ): void {
    this.decorSprites.forEach((sprite, key) => {
      if (!visibleDecor.has(key)) {
        sprite.destroy();
        this.decorSprites.delete(key);
      }
    });

    this.fishSprites.forEach((sprite, key) => {
      if (!visibleFish.has(key)) {
        sprite.destroy();
        this.fishSprites.delete(key);
      }
    });

    this.fishHitAreas.forEach((hitArea, key) => {
      if (!visibleFish.has(key)) {
        hitArea.destroy();
        this.fishHitAreas.delete(key);
      }
    });

    this.rockHitAreas.forEach((hitArea, key) => {
      if (!visibleRocks.has(key)) {
        hitArea.destroy();
        this.rockHitAreas.delete(key);
      }
    });
  }

  public isOceanRockAtTile(tileX: number, tileY: number): boolean {
    const decorSeed: number = this.getDeterministicRandom(tileX, tileY);
    return decorSeed < 0.02;
  }

  private getDeterministicRandom(tileX: number, tileY: number): number {
    const seed = tileX * 928371 + tileY * 364479 + 1337;
    const sinVal = Math.sin(seed) * 10000;
    return sinVal - Math.floor(sinVal);
  }

  public destroy(): void {
    this.renderedTiles.forEach((sprite) => sprite.destroy());
    this.renderedTiles.clear();
    this.decorSprites.forEach((sprite) => sprite.destroy());
    this.decorSprites.clear();
    this.fishSprites.forEach((sprite) => sprite.destroy());
    this.fishSprites.clear();
    this.fishHitAreas.forEach((hitArea) => hitArea.destroy());
    this.fishHitAreas.clear();
    this.rockHitAreas.forEach((hitArea) => hitArea.destroy());
    this.rockHitAreas.clear();
    if (this.container) {
      this.container.destroy();
    }
    if (this.decorContainer) {
      this.decorContainer.destroy();
      this.decorContainer = null;
    }
    if (this.fishContainer) {
      this.fishContainer.destroy();
      this.fishContainer = null;
    }
  }
}
